<template>
  <section v-if="visible" class="cda-staged-notes" data-test="cda-staged-notes">
    <div v-if="notes.length > 0" class="notes">
      <h6 class="notes-title">Look at these before you save</h6>
      <p class="notes-hint" data-test="cda-notes-hint">
        Save still saves every extracted item as it stands. An item the document left without a
        date is saved without one; an item whose code this system does not know is saved as an
        uncoded item. {{ canChange ? 'Give an item its date, or leave it out of the chart, first.' : '' }}
      </p>
      <ul class="list-unstyled notes-list">
        <li v-for="note in notes" :key="noteKey(note)" class="note" data-test="cda-staged-note">
          <div class="note-head">
            <strong class="note-label">{{ note.label || 'An item' }}</strong>
            <span class="note-kind">{{ kindName(note) }}</span>
          </div>
          <div class="note-reason" data-test="cda-note-reason">{{ reasonText(note) }}</div>
          <div v-if="canChange" class="note-actions">
            <label v-if="note.reason === 'missing_date' && note.field" class="note-date">
              <span class="note-date-label">Date</span>
              <!-- Not shut while a change is under way: a date typed meanwhile waits its
                   turn (sendDate), and a field that is shut takes the clinician's focus,
                   and the rest of the year being typed, with it. -->
              <input
                type="date"
                class="form-control form-control-sm"
                :data-test="'cda-note-date-' + note.kind + '-' + note.id"
                :aria-label="'Date for ' + (note.label || 'this item')"
                @change="setDate(note, $event.target.value)"
                @blur="leaveDate(note, $event.target.value)"
              />
            </label>
            <button
              type="button"
              class="btn btn-sm btn-outline-danger"
              data-test="cda-drop-item"
              :disabled="busy"
              @click="leaveOut(note)"
            >
              Leave out of the chart
            </button>
          </div>
        </li>
      </ul>
    </div>

    <div v-if="notStaged.length > 0" class="not-staged" data-test="cda-not-staged">
      <h6 class="notes-title">Left out of this import ({{ notStagedTotal }})</h6>
      <p class="notes-hint">The document holds these, and none of them could be made an item here.</p>
      <ul class="list-unstyled notes-list">
        <li v-for="(entry, index) in notStaged" :key="'ns' + index" class="note" data-test="cda-not-staged-entry">
          <strong class="note-label">{{ notStagedLabel(entry) }}</strong>
          <span v-if="entry.section" class="note-kind">{{ entry.section }}</span>
          <div class="note-reason">{{ notStagedReason(entry) }}</div>
        </li>
      </ul>
    </div>

    <div v-if="canChange" class="discard">
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        data-test="cda-discard-all"
        :disabled="busy"
        @click="discardAll"
      >
        Discard all extracted items
      </button>
    </div>
    <p v-else class="notes-readonly" data-test="cda-notes-readonly">
      You do not have permission to change these items.
    </p>

    <p v-if="error" class="notes-error" role="alert" data-test="cda-notes-error">{{ error }}</p>
  </section>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { PRIVILEGES } from '@/constants/privileges';
import { usePrivilegesStore } from '@/stores/privileges';
import { useChartCdaStagedItemsStore } from '@/stores/chartCdaStagedItems';

/** One item of the staged set (a note, or a date for it): ids are per kind. */
const itemKey = ({ kind, id }) => `${kind}-${id}`;

/**
 * What an entry the import could not read held (`not_staged` `element`, the C-CDA
 * element), in a clinician's words. An element not named here is not described.
 */
const HELD_ELEMENTS = {
  act: 'an act',
  encounter: 'an encounter',
  observation: 'an observation',
  observationMedia: 'an image',
  organizer: 'a group of results',
  procedure: 'a procedure',
  regionOfInterest: 'a marked region of an image',
  substanceAdministration: 'a medication administration',
  supply: 'a supply',
};

/**
 * Whether the year of a date field's value is still being typed. Chrome reports
 * a `change` for every digit typed into the year (typing 03042026 gives
 * 0002-03-04, 0020-03-04, 0202-03-04 and then 2026-03-04), and any browser that
 * does the same is read the same way. Nothing in a chart is dated before the
 * year 1000 (the API takes none before 1850 - Documents/StagedDate), so a year
 * that still starts with 0 is one on its way, not a date to send.
 */
const yearIsUnfinished = (value) => /^0/.test(value);

/**
 * The review of an open, unsaved C-CDA's extracted items: the ones the document
 * left undated or uncoded, and what a clinician may do about them before Save.
 *
 * **They are notes, not blockers** (D6 default 2026-10-21). Main commits every
 * staged item whether or not it has a date or a known code, so Save is always
 * available once the items are on screen; an item with no date is saved with
 * none, and one whose code the vocabulary lacks is saved as an uncoded chart
 * row. What this adds is the means to act first - the port had refused the
 * whole document while one item had either, and gave a clinician no way to
 * clear it:
 *
 *  - an undated item takes a date typed here (PATCH staged-items);
 *  - any flagged item can be left out of the chart (DELETE staged-items/{kind}/{id});
 *  - the whole set can be discarded, behind a confirm (DELETE staged-items), after
 *    which the C-CDA reads as not read and may be read again (CdaContentBody).
 *
 * The notes come from the API with every change - each answer is the set that
 * remains, read again - so a corrected item stops being flagged by the call that
 * corrected it. A change that fails is said here and changes nothing.
 *
 * **A date is sent as it is typed, once its year is complete** (a year still
 * being typed is held back - `yearIsUnfinished` - and sent when the field is
 * left, so the API's refusal of a year it will not take is still said). The field
 * stays open while a change is under way: a date typed meanwhile is kept, the
 * newest for each item, and sent when that change is settled - never dropped, as
 * a second click on a button is.
 *
 * Offered to whoever may write documents on the chart (the API checks chart
 * write on every one of these calls; hiding the buttons is the courtesy).
 */
export default {
  name: 'cda-staged-item-notes',
  data() {
    return {
      busy: false,
    };
  },
  created() {
    // Bookkeeping, not state: nothing on screen reads these.
    /** item -> the newest date typed for it while another change was under way, `{kind, id, field, value}`. */
    this.waitingDates = new Map();
    /** item -> the date its call under way carries. */
    this.sendingDates = new Map();
    /**
     * item -> the store's hold for the date waiting for it. A date that waits is a
     * change in the air as much as one being sent, and Save must not overtake it
     * (chartCdaStagedItems `hold`).
     */
    this.waitingHolds = new Map();
    /** The items whose last change was a year still being typed. */
    this.heldDates = new Set();
  },
  computed: {
    ...mapGetters('login/cda', {
      cdaId: 'getCdaId',
      isSaved: 'isCdaSaved',
      extractionShown: 'isCdaExtractionShown',
      notes: 'getOpenCdaReviewNotes',
      notStaged: 'getOpenCdaNotStaged',
    }),
    /** An open C-CDA whose proposals are on screen and not yet in the chart. */
    visible() {
      return !this.isSaved && this.extractionShown;
    },
    canChange() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.UPLOAD_DOCUMENTS);
    },
    /** What the last change that failed said, for the open C-CDA. */
    error() {
      return useChartCdaStagedItemsStore().errors[this.cdaId] || '';
    },
    /**
     * How many entries the document left out: the API lists the first few
     * hundred and ends a longer list with one line, `more`, that counts the
     * rest - so the heading counts what the document did, not what the list shows.
     * The lines for what the import's bounds left unread (`over_limit`) are
     * counts too: a line standing for twelve entries is twelve.
     */
    notStagedTotal() {
      return this.notStaged.reduce((total, entry) => total + (entry.count > 0 ? entry.count : 1), 0);
    },
  },
  methods: {
    ...mapActions('login/cda', [
      'correctStagedItem',
      'dropStagedItem',
      'discardStagedItems',
    ]),
    noteKey(note) {
      return `${note.kind}-${note.id}-${note.reason}`;
    },
    kindName(note) {
      if (note.kind === 'diagnoses') return note.isAllergy ? 'Allergy' : 'Problem';

      return {
        medications: 'Medication',
        vaccinations: 'Vaccination',
        procedures: 'Procedure',
        measurements: 'Result',
      }[note.kind] || 'Item';
    },
    notStagedLabel(entry) {
      if (entry.reason === 'more') return `And ${entry.count} more`;
      if (entry.reason === 'over_limit') {
        const noun = entry.unit === 'sections' ? ['section', 'sections'] : ['entry', 'entries'];

        return `${entry.count} ${entry.count === 1 ? noun[0] : noun[1]} not read`;
      }

      return entry.label || 'An entry with no name';
    },
    notStagedReason(entry) {
      if (entry.reason === 'more') return 'The list stops here. The document holds the rest, left out for the same reasons.';
      if (entry.reason === 'over_limit') {
        return entry.unit === 'sections'
          ? 'The import reads only so many sections of one document. The document holds the rest, unread.'
          : 'The import reads only so many entries of one section. The document holds the rest, unread.';
      }
      if (entry.reason === 'unnamed') return 'It gives no code, name or text to call it by.';
      if (entry.reason === 'uncoded_result') {
        return `A result needs a code this system knows, and ${entry.code || 'its code'} is not one.`;
      }
      // The document says it is NOT so ("no known allergies"): importing it would
      // put the opposite of the record on the chart.
      if (entry.reason === 'negated') {
        return 'The document says this is not so (for example, no known allergies), so it was left out rather than added as if it were.';
      }
      // A procedure or an encounter the document only plans, requests or books (main stages only those it says
      // happened): added, it would read as done on the chart.
      if (entry.reason === 'planned') {
        return 'The document lists this as planned or requested, not as done, so it was left out rather than added as if it had happened.';
      }
      // A procedure or an encounter that gives no status, or one this system does not know: read and named, but neither
      // said to be done nor to be planned. Not "unreadable" - that would say the import found nothing in an entry the
      // screen itself shows holding a procedure.
      if (entry.reason === 'not_performed') {
        return 'The document does not say this was done (its status is missing or not one this system knows), so it was left out rather than added as if it had happened.';
      }
      if (entry.reason === 'unreadable') {
        const held = Object.prototype.hasOwnProperty.call(HELD_ELEMENTS, entry.element) ? HELD_ELEMENTS[entry.element] : '';

        return `The import found nothing in it that it can take as a record of this kind${held ? ` (it holds ${held})` : ''}. The document still holds it.`;
      }

      return 'It could not be imported.';
    },
    reasonText(note) {
      if (note.reason === 'missing_date') return 'The document gives no date for this.';
      if (note.reason === 'uncoded') return 'No code this system knows; it will be saved as an uncoded item.';

      return note.reason;
    },
    /**
     * One change at a time: a second click while the first is under way is
     * ignored. A date typed meanwhile is not a click - it waits (`waitingDates`)
     * and is sent as soon as this change is settled, whether it worked or not.
     */
    async run(change) {
      if (this.busy) return false;

      this.busy = true;

      try {
        return await change();
      } finally {
        this.busy = false;
        this.sendNextWaitingDate();
      }
    },
    /** Send the next date that waited for the change that has just settled; `run` then sends the one after. */
    sendNextWaitingDate() {
      const next = this.waitingDates.values().next();

      return next.done ? Promise.resolve(false) : this.correctDate(next.value);
    },
    /** Correct one item's date (`{kind, id, field, value}`) as the change under way, remembering which date is on its way. */
    correctDate(date) {
      const key = itemKey(date);

      const sent = this.run(async () => {
        this.sendingDates.set(key, date.value);

        try {
          return await this.correctStagedItem({
            kind: date.kind,
            id: date.id,
            changes: { [date.field]: date.value },
          });
        } finally {
          this.sendingDates.delete(key);
        }
      });

      // Only now that its call is under way (the store holds it from the moment
      // it is started): a date taken off the queue first left a gap in which
      // nothing was in the air, and a Save asked for then went ahead.
      this.stopWaiting(key);

      return sent;
    },
    /** A date to send when the change under way is settled; the newest for an item replaces an older one. */
    startWaiting(key, date) {
      this.waitingDates.set(key, date);

      if (!this.waitingHolds.has(key)) {
        this.waitingHolds.set(key, useChartCdaStagedItemsStore().hold(this.cdaId));
      }
    },
    /** The item's date no longer waits: it is on its way, or the field now shows the one that is. */
    stopWaiting(key) {
      this.waitingDates.delete(key);

      const release = this.waitingHolds.get(key);

      this.waitingHolds.delete(key);
      if (release) release();
    },
    /** The date field changed. */
    setDate(note, value) {
      this.heldDates.delete(itemKey(note));

      // A cleared field is not a date to send.
      if (!value || !note.field) return Promise.resolve(false);

      // A year still being typed: wait for the rest of it. If it is the last the
      // clinician types, `leaveDate` sends it.
      if (yearIsUnfinished(value)) {
        this.heldDates.add(itemKey(note));

        return Promise.resolve(false);
      }

      return this.sendDate(note, value);
    },
    /**
     * The date field was left. A year that was still unfinished at its last
     * change is what the clinician ended on (2026 mistyped as 0226), so it is
     * sent now and the API says whether it will do. Nothing is sent for a field
     * whose date went out when it changed.
     */
    leaveDate(note, value) {
      if (!this.heldDates.delete(itemKey(note)) || !value || !note.field) return Promise.resolve(false);

      return this.sendDate(note, value);
    },
    sendDate(note, value) {
      const date = { kind: note.kind, id: note.id, field: note.field, value };
      const key = itemKey(date);

      // Another change is under way: this waits for it. Only the newest date for
      // an item is kept - it is the one the field now shows - and none at all if
      // that is the date the call under way already carries.
      if (this.busy) {
        if (this.sendingDates.get(key) === value) this.stopWaiting(key);
        else this.startWaiting(key, date);

        return Promise.resolve(false);
      }

      return this.correctDate(date);
    },
    async leaveOut(note) {
      const confirmed = await this.confirm(
        'Leave out of the chart',
        `Leave "${note.label || 'this item'}" out of the chart? Nothing of it is saved. Reading the C-CDA again brings it back.`,
        'Yes, leave it out',
      );

      if (!confirmed) return false;

      return this.run(() => this.dropStagedItem({ kind: note.kind, id: note.id }));
    },
    async discardAll() {
      const confirmed = await this.confirm(
        'Discard all extracted items',
        'Discard everything extracted from this CDA? The document stays; nothing is saved to the chart, and it can be read again.',
        'Yes, discard them',
      );

      if (!confirmed) return false;

      return this.run(() => this.discardStagedItems());
    },
    /** The page's own confirm (SweetAlert, as Save and Delete use). */
    async confirm(title, text, confirmButtonText) {
      const result = await this.$swal({
        title,
        text,
        type: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#3085d6',
        confirmButtonColor: '#d33',
        confirmButtonText,
        reverseButtons: true,
      });

      return Boolean(result && result.value);
    },
  },
};
</script>

<style scoped lang="scss">
  .cda-staged-notes {
    margin: 0 0 1rem;
    font-size: 0.9rem;
  }
  .notes-title {
    margin: 0 0 0.25rem;
  }
  .notes-hint {
    margin: 0 0 0.5rem;
    color: #6c757d;
    font-size: 0.85rem;
  }
  .notes-list {
    margin: 0 0 0.75rem;
  }
  .note {
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
    border-left: 3px solid #f0ad4e;
    background: rgba(240, 173, 78, 0.08);
  }
  .note-kind {
    margin-left: 0.5rem;
    color: #6c757d;
    font-size: 0.8rem;
  }
  .note-reason {
    color: #6c757d;
  }
  .note-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.35rem;
  }
  .note-date {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin: 0;

    input {
      width: auto;
    }
  }
  .notes-error {
    margin: 0.5rem 0 0;
    color: #c0392b;
  }
  .notes-readonly {
    margin: 0;
    color: #6c757d;
  }
</style>
